import i18n from "@/i18n";
import { ColumnDef } from "@tanstack/react-table";

export const SHARED_PORTFOLIO_COLUMN_ORDER = [
  "id",
  "client",
  "vehicle",
  "reporting_currency",
  "account_name",
  "name",
  "account_type",
  "currency",
  "code",
  "bank_name",
  "bank_connection",
] as const;

export type SharedPortfolioColumnKey =
  (typeof SHARED_PORTFOLIO_COLUMN_ORDER)[number];

const SHARED_PORTFOLIO_COLUMN_DEFAULT_LABELS: Record<
  SharedPortfolioColumnKey,
  string
> = {
  id: "Id",
  client: "Client",
  vehicle: "Vehicle",
  reporting_currency: "Reporting currency",
  account_name: "Account name",
  name: "Name",
  account_type: "Account type",
  currency: "Currency",
  code: "Code",
  bank_name: "Bank",
  bank_connection: "Bank connection",
};

export function getSharedPortfolioColumnHeader(
  key: SharedPortfolioColumnKey,
): string {
  return i18n.t(`portfolios.columns.${key}`, {
    defaultValue: SHARED_PORTFOLIO_COLUMN_DEFAULT_LABELS[key],
  });
}

export function getSharedPortfolioBooleanHeader(
  key: Extract<SharedPortfolioColumnKey, "bank_connection">,
) {
  return <div className="text-center">{getSharedPortfolioColumnHeader(key)}</div>;
}

type SharedPortfolioFieldMap = Partial<
  Record<SharedPortfolioColumnKey, string>
>;

type BuildSharedPortfolioColumnsArgs<TData, TValue> = {
  booleanCell?: ColumnDef<TData, TValue>["cell"];
  fields: SharedPortfolioFieldMap;
};

export function buildSharedPortfolioColumns<TData, TValue>({
  booleanCell,
  fields,
}: BuildSharedPortfolioColumnsArgs<TData, TValue>): ColumnDef<TData, TValue>[] {
  const columns: ColumnDef<TData, TValue>[] = [];

  SHARED_PORTFOLIO_COLUMN_ORDER.forEach((key) => {
    const accessorKey = fields[key];
    if (!accessorKey) return;

    if (key === "bank_connection") {
      columns.push(
        {
          accessorKey,
          header: () => getSharedPortfolioBooleanHeader(key),
          cell: booleanCell,
        } satisfies ColumnDef<TData, TValue>,
      );
      return;
    }

    columns.push(
      {
        accessorKey,
        header: () => getSharedPortfolioColumnHeader(key),
      } satisfies ColumnDef<TData, TValue>,
    );
  });

  return columns;
}

export function withPortfolioActions<TData, TValue>(
  columns: ColumnDef<TData, TValue>[],
  actions: ColumnDef<TData, TValue>[],
): ColumnDef<TData, TValue>[] {
  return [...columns, ...actions];
}
